import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Home, Lock, Loader2, Eye, EyeOff, ShieldCheck, Smartphone, KeyRound } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginForm = ({ onLogin, logoutReason }) => {
  const [mode, setMode] = useState("password"); // "password" | "mobile"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  // Mandatory 2FA enrollment (privileged roles with no authenticator yet)
  const [enroll, setEnroll] = useState(null); // { qr_uri, secret, enroll_token }
  const [enrollCode, setEnrollCode] = useState("");
  // Mobile OTP login
  const [mobile, setMobile] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const finishLogin = (data) => {
    onLogin({
      username: data.username || username,
      token: data.token,
      role: data.role,
      name: data.name || data.username || username,
      force_password_change: data.force_password_change || false,
      session_timeout_minutes: data.session_timeout_minutes || 30,
      allowed_tabs: data.allowed_tabs || [],
      role_label: data.role_label || data.role,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await axios.post(
        `${API_URL}/api/auth/login`,
        { username, password, totp_code: totpCode, remember_me: rememberMe },
        { withCredentials: true }
      );
      if (res.data.status === "2fa_required") {
        setNeeds2FA(true);
        setLoading(false);
        return;
      }
      if (res.data.status === "2fa_enrollment_required") {
        setEnroll({ qr_uri: res.data.qr_uri, secret: res.data.secret, enroll_token: res.data.enroll_token });
        setLoading(false);
        return;
      }
      finishLogin(res.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.response?.data?.error;
      if (status === 429) {
        setError("Too many login attempts. Please wait a minute and try again.");
      } else {
        setError(detail || "Invalid credentials. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/2fa/enroll`,
        { enroll_token: enroll.enroll_token, code: enrollCode },
        { withCredentials: true }
      );
      finishLogin(res.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) setError("Too many attempts. Please wait a minute.");
      else setError(detail || "Invalid code. Try again.");
      setLoading(false);
    }
  };

  const sendStaffOtp = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/staff/send-otp`,
        { mobile },
        { withCredentials: true }
      );
      setOtpSent(true);
      setResendIn(res.data?.resend_in || 60);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      setError(detail || (status === 429 ? "Too many requests. Please wait and try again." : "Couldn't send OTP."));
    } finally {
      setLoading(false);
    }
  };

  const verifyStaffOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/staff/verify-otp`,
        { mobile, code: otpCode, remember_me: rememberMe },
        { withCredentials: true }
      );
      finishLogin(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || "Invalid code. Please try again.");
      setLoading(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError("");
    setNeeds2FA(false);
    setOtpSent(false);
    setOtpCode("");
  };

  // ── Mandatory 2FA enrollment screen ──────────────────────────────
  if (enroll) {
    return (
      <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-md" data-testid="admin-2fa-enroll-form">
          <div className="text-center mb-6">
            <ShieldCheck className="mx-auto text-[#DC283C] mb-2" size={40} />
            <h1 className="text-2xl font-bold text-[#1E1B4B] font-['Outfit']">Set up two-factor auth</h1>
            <p className="text-[#475569] mt-2 text-sm">Your role requires 2FA. Scan this QR code with Google Authenticator (or any TOTP app), then enter the 6-digit code.</p>
          </div>
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-4 text-sm" data-testid="enroll-error">{error}</div>
          )}
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-white rounded-xl border border-gray-200">
              <QRCodeCanvas value={enroll.qr_uri} size={180} data-testid="enroll-qr" />
            </div>
          </div>
          <p className="text-xs text-center text-[#94a3b8] mb-4">
            Can't scan? Enter this key manually:<br />
            <span className="font-mono text-[#1E1B4B] break-all" data-testid="enroll-secret">{enroll.secret}</span>
          </p>
          <form onSubmit={handleEnrollSubmit} className="space-y-4">
            <input
              type="text"
              value={enrollCode}
              onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#DC283C] outline-none text-center text-xl tracking-[0.5em] font-mono"
              placeholder="000000"
              maxLength={6}
              required
              data-testid="enroll-totp"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#DC283C] to-[#FF9F1C] text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="enroll-submit"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : null}
              Verify & Enable 2FA
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-md" data-testid="admin-login-form">
        {/* Back to Home Button */}
        <Link
          to="/"
          className="flex items-center gap-2 text-[#475569] hover:text-[#DC283C] mb-6 transition-colors"
          data-testid="back-to-home"
        >
          <Home size={18} />
          <span>Back to Home</span>
        </Link>

        <div className="text-center mb-6">
          <img src="https://customer-assets.emergentagent.com/job_party-in-2hrs/artifacts/47ayyz1k_SUMIT%20DOOZYGO%20LOGO%20-%20AUG%202023.png" alt="DoozyGo" className="h-20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1E1B4B] font-['Outfit']">DoozyGo Admin</h1>
          <p className="text-[#475569] mt-2">Sign in to manage bookings</p>
        </div>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-gray-100 mb-6" data-testid="login-mode-toggle">
          <button
            type="button"
            onClick={() => switchMode("password")}
            className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${mode === "password" ? "bg-white text-[#1E1B4B] shadow-sm" : "text-[#94a3b8] hover:text-[#1E1B4B]"}`}
            data-testid="login-mode-password"
          >
            <KeyRound size={15} /> Email &amp; Password
          </button>
          <button
            type="button"
            onClick={() => switchMode("mobile")}
            className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${mode === "mobile" ? "bg-white text-[#1E1B4B] shadow-sm" : "text-[#94a3b8] hover:text-[#1E1B4B]"}`}
            data-testid="login-mode-mobile"
          >
            <Smartphone size={15} /> Mobile OTP
          </button>
        </div>

        {logoutReason && (
          <div className="bg-yellow-50 text-yellow-700 px-4 py-3 rounded-xl mb-4 text-sm">
            {logoutReason}
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-4 text-sm" data-testid="login-error">
            {error}
          </div>
        )}

        {mode === "password" ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1E1B4B] mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#DC283C] focus:ring-2 focus:ring-[#DC283C]/20 outline-none transition-all"
                placeholder="Enter username"
                required
                data-testid="login-username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1E1B4B] mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#DC283C] focus:ring-2 focus:ring-[#DC283C]/20 outline-none transition-all pr-12"
                  placeholder="Enter password"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#94a3b8] hover:text-[#1E1B4B] transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {needs2FA && (
              <div>
                <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
                  <Lock size={14} /> 2FA Code
                </label>
                <input
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#DC283C] focus:ring-2 focus:ring-[#DC283C]/20 outline-none transition-all text-center text-xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  maxLength={6}
                  required
                  data-testid="login-totp"
                />
                <p className="text-xs text-[#94a3b8] mt-1">Enter the 6-digit code from your authenticator app</p>
              </div>
            )}

            <label className="flex items-center gap-2 select-none cursor-pointer text-sm text-[#475569]" data-testid="login-remember-me-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-[#DC283C] focus:ring-[#DC283C]/40 accent-[#DC283C]"
                data-testid="login-remember-me"
              />
              Remember me on this device for 7 days
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#DC283C] to-[#FF9F1C] text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : null}
              {needs2FA ? "Verify & Sign In" : "Sign In"}
            </button>
          </form>
        ) : (
          <form onSubmit={otpSent ? verifyStaffOtp : sendStaffOtp} className="space-y-4" data-testid="mobile-otp-form">
            <div>
              <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
                <Smartphone size={14} /> Mobile Number
              </label>
              <input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                disabled={otpSent}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#DC283C] focus:ring-2 focus:ring-[#DC283C]/20 outline-none transition-all disabled:bg-gray-50 disabled:text-[#94a3b8]"
                placeholder="e.g. +91 98123 45678"
                required
                data-testid="otp-mobile-input"
              />
              <p className="text-xs text-[#94a3b8] mt-1">Use the mobile your admin registered for OTP login.</p>
            </div>

            {otpSent && (
              <div>
                <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
                  <Lock size={14} /> OTP Code
                </label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#DC283C] focus:ring-2 focus:ring-[#DC283C]/20 outline-none transition-all text-center text-xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  maxLength={6}
                  required
                  data-testid="otp-code-input"
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-[#94a3b8]">Code expires in 5 minutes</p>
                  <button
                    type="button"
                    onClick={sendStaffOtp}
                    disabled={resendIn > 0 || loading}
                    className="text-xs font-semibold text-[#DC283C] disabled:text-[#94a3b8] disabled:cursor-not-allowed hover:underline"
                    data-testid="otp-resend-btn"
                  >
                    {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                  </button>
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 select-none cursor-pointer text-sm text-[#475569]">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-[#DC283C] focus:ring-[#DC283C]/40 accent-[#DC283C]"
                data-testid="otp-remember-me"
              />
              Remember me on this device for 7 days
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#DC283C] to-[#FF9F1C] text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="otp-submit"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : null}
              {otpSent ? "Verify & Sign In" : "Send OTP"}
            </button>

            {otpSent && (
              <button
                type="button"
                onClick={() => { setOtpSent(false); setOtpCode(""); setError(""); }}
                className="w-full text-sm text-[#94a3b8] hover:text-[#1E1B4B]"
                data-testid="otp-change-number"
              >
                Use a different number
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginForm;
